import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Search, X, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { entryService, EntryRead, TagRead, IntegrationInfo } from '@/services/entry-service';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';

const ENTRIES_PER_PAGE = 20;

const EntriesView = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  
  const [entries, setEntries] = useState<EntryRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [afterId, setAfterId] = useState<number | null>(null);
  
  // Filter states
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedIntegration, setSelectedIntegration] = useState<string>('all');
  const [tagInput, setTagInput] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState<TagRead[]>([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [integrations, setIntegrations] = useState<IntegrationInfo[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Initialize filters from URL params
  useEffect(() => {
    const tags = searchParams.get('tags');
    const integration = searchParams.get('integration');
    const afterIdParam = searchParams.get('afterId');
    
    if (tags) {
      setSelectedTags(tags.split(','));
    } else {
      setSelectedTags([]);
    }
    
    if (integration) {
      setSelectedIntegration(integration);
    } else {
      setSelectedIntegration('all');
    }
    
    if (afterIdParam) {
      setAfterId(parseInt(afterIdParam));
    } else {
      setAfterId(null);
    }
    setIsInitialized(true);
  }, [searchParams]);
  
  useEffect(() => {
    loadIntegrations();
  }, []);
  
  useEffect(() => {
    if (isInitialized) {
      loadEntries();
    }
  }, [selectedTags, selectedIntegration, afterId, isInitialized]);
  
  useEffect(() => {
    if (tagInput.length > 0) {
      loadTagSuggestions();
    } else {
      setTagSuggestions([]);
      setShowTagSuggestions(false);
    }
  }, [tagInput]);
  
  const loadEntries = async () => {
    try {
      setLoading(true);
      const data = await entryService.getEntries({
        tags: selectedTags.length > 0 ? selectedTags.join(',') : undefined,
        integration: selectedIntegration === 'all' ? undefined : selectedIntegration,
        limit: ENTRIES_PER_PAGE,
        afterId: afterId || undefined,
      });
      
      // Reset entries when loading from scratch (filters changed or initial load)
      setEntries(data);
      setHasMore(data.length === ENTRIES_PER_PAGE);
      
      // Update afterId for next pagination
      if (data.length > 0) {
        const lastEntry = data[data.length - 1];
        setAfterId(lastEntry.id || null);
      } else {
        setAfterId(null);
      }
      
      // Update URL params
      const params = new URLSearchParams();
      if (selectedTags.length > 0) params.set('tags', selectedTags.join(','));
      if (selectedIntegration && selectedIntegration !== 'all') params.set('integration', selectedIntegration);
      if (afterId) params.set('afterId', afterId.toString());
      setSearchParams(params);
    } catch (error) {
      console.error('Error loading entries:', error);
      toast({
        title: 'Error loading entries',
        description: 'Failed to load entries',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };
  
  const loadIntegrations = async () => {
    try {
      const data = await entryService.getIntegrations();
      setIntegrations(data.filter(integration => integration.enabled));
    } catch (error) {
      console.error('Error loading integrations:', error);
    }
  };
  
  const loadTagSuggestions = async () => {
    try {
      const tags = await entryService.getTags(tagInput, 5);
      setTagSuggestions(tags.filter(tag => !selectedTags.includes(tag.name)));
      setShowTagSuggestions(tags.length > 0);
    } catch (error) {
      console.error('Error loading tag suggestions:', error);
    }
  };
  
  const handleAddTag = (tagName: string) => {
    if (!selectedTags.includes(tagName)) {
      setSelectedTags([...selectedTags, tagName]);
      setTagInput('');
      setShowTagSuggestions(false);
      setAfterId(null); // Reset pagination
      setEntries([]); // Reset entries to trigger fresh load
    }
  };
  
  const handleRemoveTag = (tagName: string) => {
    setSelectedTags(selectedTags.filter(tag => tag !== tagName));
    setAfterId(null); // Reset pagination
    setEntries([]); // Reset entries to trigger fresh load
  };
  
  const handleClearFilters = () => {
    setSelectedTags([]);
    setSelectedIntegration('all');
    setAfterId(null);
    setEntries([]); // Reset entries to trigger fresh load
  };
  
  const loadMoreEntries = async () => {
    if (loadingMore || !hasMore) return;
    
    const lastEntry = entries[entries.length - 1];
    if (!lastEntry) return;
    
    try {
      setLoadingMore(true);
      const data = await entryService.getEntries({
        tags: selectedTags.length > 0 ? selectedTags.join(',') : undefined,
        integration: selectedIntegration === 'all' ? undefined : selectedIntegration,
        limit: ENTRIES_PER_PAGE,
        afterId: lastEntry.id || undefined,
      });
      
      setEntries(prev => [...prev, ...data]);
      setHasMore(data.length === ENTRIES_PER_PAGE);
      
      if (data.length > 0) {
        const newLastEntry = data[data.length - 1];
        setAfterId(newLastEntry.id || null);
      }
    } catch (error) {
      console.error('Error loading more entries:', error);
      toast({
        title: 'Error loading more entries',
        description: 'Failed to load more entries',
        variant: 'destructive',
      });
    } finally {
      setLoadingMore(false);
    }
  };

  const { loadMoreRef } = useInfiniteScroll({
    hasMore,
    isLoading: loadingMore,
    onLoadMore: loadMoreEntries,
  });
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };
  
  return (
    <div className="container mx-auto py-10 px-4">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Entries</h1>
          <Button onClick={() => navigate('/dashboard')} variant="outline">
            Back to Dashboard
          </Button>
        </div>
        
        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filters</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Tag Filter */}
            <div>
              <label className="text-sm font-medium mb-2 block">Tags</label>
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2 mb-2">
                  {selectedTags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="relative">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add tag filter..."
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && tagInput.trim()) {
                          e.preventDefault();
                          handleAddTag(tagInput.trim());
                        }
                      }}
                      className="h-9"
                    />
                  </div>
                  
                  {/* Tag suggestions */}
                  {showTagSuggestions && tagSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg">
                      {tagSuggestions.map((tag) => (
                        <button
                          key={tag.id}
                          onClick={() => handleAddTag(tag.name)}
                          className="w-full text-left px-3 py-2 hover:bg-muted transition-colors text-sm"
                        >
                          {tag.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Integration Filter */}
            <div>
              <label className="text-sm font-medium mb-2 block">Integration</label>
              <Select value={selectedIntegration} onValueChange={(value) => {
                setSelectedIntegration(value);
                setAfterId(null);
                setEntries([]); // Reset entries to trigger fresh load
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="All integrations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All integrations</SelectItem>
                  {integrations.map((integration) => (
                    <SelectItem key={integration.name} value={integration.name}>
                      {integration.name.charAt(0).toUpperCase() + integration.name.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Clear Filters */}
            {(selectedTags.length > 0 || selectedIntegration !== 'all') && (
              <Button
                onClick={handleClearFilters}
                variant="outline"
                size="sm"
                className="w-full"
              >
                Clear Filters
              </Button>
            )}
          </CardContent>
        </Card>
        
        {/* Entries List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : entries.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No entries found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {entries.map((entry) => {
              const latestAnalysis = entry.analyses[0];
              const notionId = entry.external_ids?.notion?.all || entry.external_ids?.notion?.[Object.keys(entry.external_ids.notion).find(k => k !== 'all') || ''];
              const notionUrl = notionId ? `https://notion.so/${notionId.replace(/-/g, '')}` : null;
              const dropboxUrl = entry.external_ids?.file_url;
              
              return (
                <Card
                  key={entry.id}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/entry/${entry.id}`)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">
                          {latestAnalysis?.title || `Entry #${entry.id}`}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2">
                          <span>{formatDate(entry.created_at || '')}</span>
                          <Badge variant="outline" className="text-xs">
                            ID: {entry.id}
                          </Badge>
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        {entry.deleted && (
                          <Badge variant="destructive">Deleted</Badge>
                        )}
                        {latestAnalysis?.integration && (
                          <Badge variant="secondary">{latestAnalysis.integration}</Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {latestAnalysis?.transcription && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {latestAnalysis.transcription}
                      </p>
                    )}
                    
                    {latestAnalysis?.tags && latestAnalysis.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {latestAnalysis.tags.map((tag) => (
                          <Badge 
                            key={tag.id} 
                            variant="outline" 
                            className="text-xs cursor-pointer hover:bg-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddTag(tag.name);
                            }}
                          >
                            {tag.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex gap-2 mt-2">
                      {dropboxUrl && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(dropboxUrl, '_blank');
                          }}
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          Dropbox
                        </Button>
                      )}
                      {notionUrl && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(notionUrl, '_blank');
                          }}
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          Notion
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {hasMore && (
              <div ref={loadMoreRef} className="py-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EntriesView;
